import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import usePostSeo from "@/hooks/usePostSeo";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    api.get("/blog/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  usePostSeo({
    title: "Regulens Blog — Plain-English U.S. Shopify Compliance Guides",
    description: "Verified guides on U.S. sales tax nexus and consumer privacy law for independent Shopify merchants. New posts weekly.",
    canonical: `${window.location.origin}/blog`,
    ogType: "website",
  });

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="blog-index-page">
        <header className="mb-12">
          <h1
            className="text-[var(--rg-text-strong)] font-bold"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontSize: "clamp(28px, 5vw, 40px)",
              lineHeight: 1.2,
              letterSpacing: "-0.5px",
            }}
            data-testid="blog-index-title"
          >
            The Regulens Blog
          </h1>
          <p className="mt-3 text-[var(--rg-text)]" style={{ fontFamily: "'Inter', sans-serif", fontSize: 17, lineHeight: 1.6 }}>
            Plain-English guides on U.S. sales tax nexus and consumer privacy law for independent Shopify merchants. New posts every week.
          </p>
        </header>

        {posts === null && (
          <div className="text-[var(--rg-muted)] text-sm" data-testid="blog-loading">Loading posts…</div>
        )}

        {posts !== null && posts.length === 0 && (
          <div
            className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-8 text-center"
            data-testid="blog-empty-state"
          >
            <p className="text-[var(--rg-text-strong)] text-lg font-semibold" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              New guides publishing soon. Check back weekly.
            </p>
            <div className="mt-4">
              <a href="/" data-testid="blog-empty-cta">
                <Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">
                  Back to Regulens
                </Button>
              </a>
            </div>
          </div>
        )}

        {posts !== null && posts.length > 0 && (
          <ul className="space-y-8" data-testid="blog-post-list">
            {posts.map((p) => (
              <li
                key={p.id}
                className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 hover:border-[var(--rg-border-hi)] transition-fast"
                data-testid={`blog-card-${p.slug}`}
              >
                <Link to={`/blog/${p.slug}`} className="block group">
                  <h2
                    className="text-[var(--rg-text-strong)] group-hover:text-[var(--rg-sage)] transition-fast"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 700,
                      fontSize: 24,
                      lineHeight: 1.25,
                      letterSpacing: "-0.3px",
                    }}
                    data-testid={`blog-card-title-${p.slug}`}
                  >
                    {p.title}
                  </h2>
                  <div className="mt-2 blog-meta flex items-center gap-3 flex-wrap">
                    <span>{formatDate(p.publish_date)}</span>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {p.reading_time_minutes || 1} min read
                    </span>
                  </div>
                  <p
                    className="mt-3 text-[var(--rg-text)]"
                    style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, lineHeight: 1.7 }}
                    data-testid={`blog-card-excerpt-${p.slug}`}
                  >
                    {p.excerpt}
                  </p>
                  <span
                    className="mt-4 inline-flex items-center gap-1 text-[var(--rg-sage)] font-semibold text-sm"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    data-testid={`blog-card-readmore-${p.slug}`}
                  >
                    Read more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
